import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ImageIcon, Download, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NftProjectArt({ user }) {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [projRes, imgRes] = await Promise.all([
        axios.get(`${API}/nft/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/nft/projects/${projectId}/images`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProject(projRes.data.project);
      setAssets(imgRes.data.assets || []);
      setMappings(imgRes.data.mappings || []);
    } catch (err) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/nft/projects/${projectId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project?.name || 'nft_project'}_export.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed — make sure items are generated first');
    }
  };

  if (loading) return <div style={{ padding: '32px', color: theme.colors.text.muted }}>Loading...</div>;
  if (!project) return <div style={{ padding: '32px', color: theme.colors.text.muted }}>Project not found</div>;

  return (
    <div data-testid="nft-project-art-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontFamily: theme.fonts.heading, fontSize: '28px', fontWeight: '700', color: theme.colors.text.primary, marginBottom: '8px' }}>
            {project.name} — Art
          </h1>
          <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
            {project.quantity} items &middot; {project.standard} &middot; {mappings.length} images mapped
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button onClick={() => navigate('/creator/nft-art/ai')} style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#A855F7' }}>
            AI Generate
          </Button>
          <Button onClick={() => navigate('/creator/nft-art/traits')} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}>
            Trait Generate
          </Button>
          <Button data-testid="export-zip-btn" onClick={handleExport} style={{ background: 'linear-gradient(135deg, #10B981, #06B6D4)', color: '#FFF' }}>
            <Download size={16} style={{ marginRight: '6px' }} /> Export ZIP
          </Button>
        </div>
      </div>

      {/* Token Mappings */}
      <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, marginBottom: '24px' }}>
        <CardHeader>
          <CardTitle style={{ color: theme.colors.text.primary }}>
            Token Image Mappings ({mappings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mappings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <ImageIcon size={36} style={{ color: theme.colors.text.muted, margin: '0 auto 12px' }} />
              <p style={{ color: theme.colors.text.muted, fontSize: '14px', marginBottom: '8px' }}>
                No images mapped to tokens yet.
              </p>
              <p style={{ color: theme.colors.text.muted, fontSize: '13px' }}>
                Use the AI Generator or Trait Generator to create images, then they'll appear here.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', maxHeight: '500px', overflowY: 'auto' }}>
              {mappings.map((m) => (
                <div
                  key={m.token_id}
                  data-testid={`mapping-token-${m.token_id}`}
                  style={{ borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.colors.borders}`, textAlign: 'center' }}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${m.image_url}`}
                    alt={`Token #${m.token_id}`}
                    style={{ width: '100%', height: '120px', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ padding: '4px 6px', background: 'rgba(0,0,0,0.03)' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: theme.colors.text.primary }}>#{m.token_id}</p>
                    <p style={{ fontSize: '10px', color: theme.colors.text.muted }}>{m.source}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
        <CardContent className="pt-5 pb-5">
          <p style={{ color: theme.colors.text.muted, fontSize: '13px' }}>
            {mappings.length > 0
              ? `When you export metadata, token JSON files will reference these ${mappings.length} mapped images. Local images will be included in the ZIP.`
              : 'Generate or upload images, then export metadata. Images without mappings will use the default image_base_url pattern.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
